import React, { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import styled from "@emotion/styled";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const Container = styled.div`
  width: ${(props) => props.width || "100%"};
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
  background-color: var(--body__background);
  border: 1px solid var(--border__color);
  border-radius: 12px;
  position: relative;
  cursor: pointer;
  min-width: 180px;
`;

const NavButton = styled.button`
  border: none;
  background: transparent;
  color: #6860ee;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 6px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(104, 96, 238, 0.1);
  }

  &:disabled {
    color: #ccc;
    cursor: not-allowed;
  }
`;

const DateText = styled.span`
  font-size: 0.875rem;
  color: var(--job-Description_Color);
  flex-grow: 1;
  font-weight: 500;
`;

const DatePicker = ({ 
  calendarClassName = "custom-calendar", 
  onDateChange, 
  disableYear = false, 
  enableArrows = true,
  enableCalendarHeader = true,
  selectedDate = null,
  placeholder = "Select Date",
  minDate = null,
  icon = null,
  width,
  style = {},
}) => {
  const [currentDate, setCurrentDate] = useState(selectedDate);
  const [isDatePickerOpen, setDatePickerOpen] = useState(false);

  useEffect(() => {
    setCurrentDate(selectedDate);
  }, [selectedDate]);

  const navigateDay = (direction) => {
    const dateToMove = currentDate || new Date();
    const newDate = new Date(dateToMove);
    newDate.setDate(newDate.getDate() + direction);
    setCurrentDate(newDate);
    onDateChange?.(newDate);
  };

  const handleDateSelect = (date) => {
    if (date instanceof Date && !isNaN(date)) {
      setCurrentDate(date);
      onDateChange?.(date);
    }
    setDatePickerOpen(false);
  };
  const CustomInput = React.forwardRef(({ value, onClick }, ref) => (
    <Container ref={ref} onClick={() => setDatePickerOpen(!isDatePickerOpen)} width={width} style={style}>
      {enableArrows && (
        <NavButton
          onClick={(e) => {
            e.stopPropagation();
            navigateDay(-1);
          }}
        >
          <ChevronLeft size={16} strokeWidth={3} />
        </NavButton>
      )}

      {icon && <img src={icon} alt="calendar-icon" style={{ width: "18px", marginRight: "8px" }} />}
      <DateText>{value || placeholder}</DateText>

      {enableArrows && (
        <NavButton
          onClick={(e) => {
            e.stopPropagation();
            navigateDay(1);
          }}
        >
          <ChevronRight size={16} strokeWidth={3} />
        </NavButton>
      )}
    </Container>
  ));

  const years = Array.from({ length: 101 }, (_, i) => moment().year() - 50 + i);
  const months = moment.months();

  return (
    <ReactDatePicker
      selected={currentDate}
      onChange={handleDateSelect}
      customInput={<CustomInput />}
      renderCustomHeader={
        enableCalendarHeader
          ? undefined
          : ({ date, changeYear, changeMonth }) => (
              <div style={{ margin: "10px", display: "flex", justifyContent: "center", gap: "10px" }}>
                <select
                  value={moment(date).month()}
                  onChange={({ target: { value } }) => changeMonth(parseInt(value))}
                  style={{ padding: '4px 8px', borderRadius: '8px', border: '1px solid var(--border__color)', background: 'var(--body__background)', color: 'var(--header__color)', cursor: 'pointer', outline: 'none' }}
                >
                  {months.map((option, index) => (
                    <option key={option} value={index}>
                      {option}
                    </option>
                  ))}
                </select>

                {!disableYear && (
                  <select
                    value={moment(date).year()}
                    onChange={({ target: { value } }) => changeYear(parseInt(value))}
                    style={{ padding: '4px 8px', borderRadius: '8px', border: '1px solid var(--border__color)', background: 'var(--body__background)', color: 'var(--header__color)', cursor: 'pointer', outline: 'none' }}
                  >
                    {years.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )
      }
      open={isDatePickerOpen}
      onClickOutside={() => setDatePickerOpen(false)}
      popperPlacement="bottom-start"
      shouldCloseOnSelect={true}
      calendarClassName={calendarClassName}
      showYearDropdown={!disableYear}
      showMonthDropdown={true}
      minDate={minDate}
      dropdownMode="select"
      todayButton="Today"
      dateFormat={disableYear ? "MMM dd" : "MMM dd, yyyy"}
    />
  );
};

export default DatePicker;

export function CustomDatePickerModal({ initialDate, onOk, onClose, maxDate, currentView, disablePast = true, top, left, zIndex = 1000 }) {

  const modalRef = React.useRef(null);
  const [hoveredDate, setHoveredDate] = React.useState(null);
  const [isReady, setIsReady] = React.useState(false);
  const [screenWidth, setScreenWidth] = React.useState(window.innerWidth);
  const [computedTop, setComputedTop] = React.useState(top);

  React.useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  React.useLayoutEffect(() => {
    if (typeof top === "number" && typeof left === "number") {
      if (modalRef.current) {
        const height = modalRef.current.offsetHeight;
        const viewportHeight = window.innerHeight;
        
        if (top + height > viewportHeight) {
          setComputedTop(Math.max(10, top - height - 50));
        } else {
          setComputedTop(top);
        }
        setIsReady(true);
      }
    } else {
      setIsReady(false);
    }
  }, [top, left, screenWidth]);

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target) && !event.target.closest('.calendarschedulerIcons')) {
        onClose();
      }
    }
    const handleScroll = () => {
      onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [onClose]);
  const getValidDate = (date) => {
    if (!date) return new Date();
    if (date instanceof Date && !isNaN(date)) return date;
    if (typeof date === 'string' || typeof date === 'number') {
      const parsedDate = new Date(date);
      if (!isNaN(parsedDate)) return parsedDate;
    }
    return new Date();
  };
   const [tempDate, setTempDate] = React.useState(() => getValidDate(initialDate));
  const [displayMonth, setDisplayMonth] = React.useState(tempDate.getMonth());
  const [displayYear, setDisplayYear] = React.useState(tempDate.getFullYear());
  const [selectedWeek, setSelectedWeek] = React.useState(null);

  React.useEffect(() => {
    const nextDate = getValidDate(initialDate);
    setTempDate(nextDate);
    setDisplayMonth(nextDate.getMonth());
    setDisplayYear(nextDate.getFullYear());
    if (currentView === 'week') {
      setSelectedWeek(nextDate);
    }
  }, [initialDate, currentView]);

  if (top === null || left === null) {
    return null;
  }

  const getWeekRange = (date) => {
    const startOfWeek = moment(date).clone().startOf('week');
    const endOfWeek = moment(date).clone().endOf('week');
    return { startOfWeek, endOfWeek };
  };

  const isInSelectedWeek = (date) => {
    if (!selectedWeek || currentView !== 'week') return false;
    const { startOfWeek, endOfWeek } = getWeekRange(selectedWeek);
    const checkDate = moment(new Date(displayYear, displayMonth, date));
    return checkDate.isBetween(startOfWeek, endOfWeek, 'day', '[]');
  };

  const daysInMonth = moment({ year: displayYear, month: displayMonth }).daysInMonth();
  const firstDayOfMonth = moment({ year: displayYear, month: displayMonth, day: 1 }).day();
  const today = moment();
  const weeks = [];
  let day = 1 - firstDayOfMonth;
  for (let w = 0; w < 6; w++) {
    const week = [];
    for (let d = 0; d < 7; d++, day++) {
      if (day < 1 || day > daysInMonth) {
        week.push(null);
      } else {
        week.push(day);
      }
    }
    weeks.push(week);
  }
  const handleDayClick = (d) => {
    if (!d) return;
    const clickedDate = new Date(displayYear, displayMonth, d);

    if (currentView === 'week') {
      setSelectedWeek(clickedDate);
      setTempDate(clickedDate);
    } else {
      setTempDate(clickedDate);
      setSelectedWeek(null);
    }
  };
  const handleOk = () => {
    onOk(tempDate);
  };
  const handleYearChange = (e) => {
    const newYear = Number(e.target.value);
    setDisplayYear(newYear);
    const maxDay = moment({ year: newYear, month: displayMonth }).daysInMonth();
    if (tempDate.getDate() > maxDay) {
      setTempDate(new Date(newYear, displayMonth, maxDay));
    } else {
      setTempDate(new Date(newYear, displayMonth, tempDate.getDate()));
    }
  };
  const handleMonthChange = (e) => {
    const newMonth = Number(e.target.value);
    setDisplayMonth(newMonth);
    const maxDay = moment({ year: displayYear, month: newMonth }).daysInMonth();
    if (tempDate.getDate() > maxDay) {
      setTempDate(new Date(displayYear, newMonth, maxDay));
    } else {
      setTempDate(new Date(displayYear, newMonth, tempDate.getDate()));
    }
  };
  const safeLeft = left !== undefined && left !== null ? Math.min(left, screenWidth - 350) : undefined;
  return (
    <div
      ref={modalRef}
      className="date-picker-modal"
      style={{
        position: "fixed",
        top: computedTop,
        left: safeLeft,
        transform: "none",
        opacity: isReady ? 1 : 0,
        transition: "opacity 0.2s ease",
        zIndex,
        backgroundColor: "var(--body__background)",
        borderRadius: "12px",
        padding: "16px",
        boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)",
        // animation: "fadeIn 0.3s ease",
        border: "1px solid rgba(0, 0, 0, 0.1)",
        minWidth: '320px',

        width: screenWidth > 1700 ? '400px' : 'auto',
        ...(screenWidth > 1700 && {
          padding: '24px',
          fontSize: '1.1rem'
        }),
        ...(screenWidth > 2400 && {
          width: '500px',
          padding: '32px',
          fontSize: '1.3rem',
          borderRadius: '20px'
        })
      }}
    >
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px', width: '100%' }}>
        <select value={displayYear} onChange={handleYearChange} className="datepicker-modal-select" style={{ minWidth: '80px' }}>
          {Array.from({ length: 201 }, (_, i) => {
            const y = moment().year() - 100 + i;
            return <option key={y} value={y}>{y}</option>;
          })}
        </select>
        <select value={displayMonth} onChange={handleMonthChange} className="datepicker-modal-select"
          style={{
            ...(window.innerWidth > 1700 && {
              padding: '8px',
              fontSize: '1rem'
            })
          }}
        >
          {moment.months().map((m, idx) => (
            <option key={m} value={idx}>{m}</option>
          ))}
        </select>
      </div>
      {/* Month/Year toggle with arrows */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '8px' }}>
        <button
          onClick={() => {
            if (displayMonth === 0) {
              setDisplayMonth(11);
              setDisplayYear(displayYear - 1);
            } else {
              setDisplayMonth(displayMonth - 1);
            }
          }}
          className="datepicker-modal-btn"
          aria-label="Previous Month"
        >
          &#8592;
        </button>
        <span style={{ fontWeight: 600, fontSize: '16px', color: 'var(--header__color)', minWidth: '120px', textAlign: 'center' }}>
          {moment.months()[displayMonth]} {displayYear}
        </span>
        <button
          onClick={() => {
            if (displayMonth === 11) {
              setDisplayMonth(0);
              setDisplayYear(displayYear + 1);
            } else {
              setDisplayMonth(displayMonth + 1);
            }
          }}
          className="datepicker-modal-btn"
          aria-label="Next Month"
        >
          &#8594;
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '10px', textAlign: 'center' }}>
        {['Su','Mo','Tu','We','Th','Fr','Sa'].map((d, i) => <div key={i} style={{ fontWeight: 600, color: 'var(--header__color)' }}>{d}</div>)}
        {weeks.flat().map((d, i) => {
          if (!d) {
            return <div key={i} style={{ opacity: 0.3 }} />;
          }

          const cellDate = new Date(displayYear, displayMonth, d);
          const todayStart = new Date();
          todayStart.setHours(0, 0, 0, 0);
          const isDisabled = (maxDate && cellDate > maxDate) || (disablePast && cellDate < todayStart);

          const isSelected =
            !isDisabled &&
            tempDate.getDate() === d &&
            displayMonth === tempDate.getMonth() &&
            displayYear === tempDate.getFullYear();

          const isInWeek = isInSelectedWeek(d);

          const isHovered = hoveredDate === d && !isDisabled;

          const isToday =
            today.date() === d &&
            today.month() === displayMonth &&
            today.year() === displayYear;

          return (
            <div
              key={i}
              onClick={() => !isDisabled && handleDayClick(d)}
              onMouseEnter={() => !isDisabled && setHoveredDate(d)}
              onMouseLeave={() => setHoveredDate(null)}
              className={[
                'calendar-day-cell',
                isSelected ? 'selected' : '',
                isInWeek ? 'week-selected' : '',
                isToday ? 'today' : '',
                isDisabled ? 'disabled' : '',
              ].filter(Boolean).join(' ')}
              style={{
                color: isDisabled
                  ? '#ccc'
                  : isSelected
                    ? 'white'
                    : isInWeek && currentView === 'week'
                      ? '#fff'
                      : isToday
                        ? '#6860ee'
                        : 'var(--header__color)',
                backgroundColor: isSelected
                  ? '#524ad7'
                  : isInWeek && currentView === 'week'
                    ? '#756fecff'
                    : isHovered
                      ? '#6860ee'
                      : 'transparent',
                opacity: isDisabled ? 0.5 : 1,
                fontWeight: isToday || isSelected ? 700 : 400,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                pointerEvents: isDisabled ? 'none' : 'auto',
                transition: 'background-color 0.15s ease',
                borderRadius: '0.5rem',
              }}
            >
              {d}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
        <button onClick={onClose} className="datepicker-modal-btn cancel">Close</button>
        <button onClick={handleOk} className="datepicker-modal-btn primary">OK</button>
      </div>
    </div>
  );
}